import { useEffect, useRef } from 'react';
import { useAI } from '../../../context/AIContext';

/**
 * Hook to auto-create a chat if none exists for the website
 * Only creates on first load when there are no chats and no current chat
 */
export const useAutoCreateChat = (websiteId) => {
  const { currentChatId, createChat, fetchChats, fetchChat, setCurrentChatId } = useAI();
  const initializedWebsiteIdRef = useRef(null);
  const normalizedWebsiteId =
    typeof websiteId === 'object'
      ? websiteId?.id || websiteId?.website_id || websiteId?.website?.id || websiteId?.value
      : websiteId;

  useEffect(() => {
    // Don't run if no websiteId or we've already initialized for this website
    if (!normalizedWebsiteId) return;
    if (initializedWebsiteIdRef.current === normalizedWebsiteId) return;
    initializedWebsiteIdRef.current = normalizedWebsiteId;

    const initialize = async () => {
      // Fetch chats for this website to check if any exist (use returned list, not state)
      const chatList = await fetchChats(normalizedWebsiteId);
      const normalizedChats = Array.isArray(chatList) ? chatList : [];

      // If a chat is selected but it doesn't belong to this website, clear it.
      const hasValidCurrentChat =
        !!currentChatId &&
        normalizedChats.some((c) => String(c?.id) === String(currentChatId));

      if (currentChatId && !hasValidCurrentChat) {
        setCurrentChatId(null);
      }

      // If there is a valid current chat but it's not loaded yet, fetch it.
      if (hasValidCurrentChat) {
        await fetchChat(currentChatId);
        return;
      }

      // If there are existing chats but none selected, auto-select the most recent.
      if (normalizedChats.length > 0) {
        const mostRecent = normalizedChats[0];
        if (mostRecent?.id) {
          setCurrentChatId(mostRecent.id);
          await fetchChat(mostRecent.id);
          return;
        }
      }

      // If still no chats, create one and auto-select it.
      const created = await createChat(normalizedWebsiteId, 'New Chat');
      if (created?.id) {
        setCurrentChatId(created.id);
        await fetchChat(created.id);
      }
    };

    initialize();
  }, [normalizedWebsiteId, currentChatId, createChat, fetchChats, fetchChat, setCurrentChatId]);
};

export default useAutoCreateChat;

